import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Camera, Send, X, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { Card } from '../ui/card';

const StaffChat = ({ user, token, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch messages on mount and every 5 seconds
  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/chat/conversation/${user.email}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async () => {
    if ((!inputText.trim() && !selectedImage) || sending) return;

    setSending(true);
    
    try {
      const response = await fetch(`${API_URL}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient_email: 'admin@butlerdirect.com',
          message_text: inputText.trim() || undefined,
          image_data: selectedImage || undefined
        })
      });

      if (response.ok) {
        // Clear input
        setInputText('');
        setSelectedImage(null);
        
        // Fetch messages immediately to show new message
        await fetchMessages();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please check your connection.');
    } finally {
      setSending(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image too large. Maximum size is 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      compressImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const compressImage = (dataUrl) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Maintain aspect ratio, max 1920x1920
      const maxDim = 1920;
      if (width > height && width > maxDim) {
        height = (height * maxDim) / width;
        width = maxDim;
      } else if (height > maxDim) {
        width = (width * maxDim) / height;
        height = maxDim;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Compress with quality 0.7
      const compressed = canvas.toDataURL('image/jpeg', 0.7);
      setSelectedImage(compressed);
    };
    img.src = dataUrl;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 shadow-sm flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">Chat with Admin</h2>
          <p className="text-xs text-gray-500">Butler Direct Support</p>
        </div>
        <MessageCircle size={24} className="text-yellow-400" />
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessageCircle size={48} className="mb-2" />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs">Send a message to start the conversation</p>
          </div>
        )}
        
        {messages.map(msg => (
          <MessageBubble 
            key={msg.id} 
            message={msg} 
            isOwn={msg.sender_email === user.email} 
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Image Preview (if selected) */}
      {selectedImage && (
        <div className="px-4 py-2 bg-white border-t">
          <div className="relative inline-block">
            <img 
              src={selectedImage} 
              alt="Preview" 
              className="h-20 w-20 object-cover rounded-lg" 
            />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Input Bar */}
      <div className="bg-white border-t px-4 py-3 flex items-center gap-2">
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleImageSelect}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0"
          disabled={sending}
        >
          <Camera size={24} className="text-gray-600" />
        </button>
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type message..."
          disabled={sending}
          className="flex-1 px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-yellow-400"
        />
        <button
          onClick={sendMessage}
          disabled={(!inputText.trim() && !selectedImage) || sending}
          className="p-2 bg-yellow-400 text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed hover:bg-yellow-500 flex-shrink-0"
        >
          {sending ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Send size={24} />
          )}
        </button>
      </div>
    </div>
  );
};

const MessageBubble = ({ message, isOwn }) => {
  const [imageExpanded, setImageExpanded] = useState(false);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <>
      <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
          isOwn 
            ? 'bg-yellow-400 text-white' 
            : 'bg-white text-gray-800 border border-gray-200'
        }`}>
          {/* Sender name (only for admin messages) */}
          {!isOwn && (
            <p className="text-xs font-semibold text-gray-600 mb-1">
              {message.sender_name}
            </p>
          )}
          
          {/* Image */}
          {message.image_data && (
            <img
              src={message.image_data}
              alt="Attachment"
              className="rounded-lg mb-2 cursor-pointer max-w-full"
              onClick={() => setImageExpanded(true)}
            />
          )}
          
          {/* Text */}
          {message.message_text && (
            <p className="text-sm whitespace-pre-wrap break-words">
              {message.message_text}
            </p>
          )}
          
          {/* Time and read status */}
          <div className={`text-xs mt-1 flex items-center gap-1 ${
            isOwn ? 'text-yellow-100 justify-end' : 'text-gray-500'
          }`}>
            <span>{formatTime(message.timestamp)}</span>
            {isOwn && (
              <span>{message.read ? '✓✓' : '✓'}</span>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {imageExpanded && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setImageExpanded(false)}
        >
          <button
            className="absolute top-4 right-4 text-white bg-gray-800 rounded-full p-2 hover:bg-gray-700"
            onClick={() => setImageExpanded(false)}
          >
            <X size={24} />
          </button>
          <img 
            src={message.image_data} 
            alt="Full size" 
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </>
  );
};

export default StaffChat;
